import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomTooltip from '../CustomToolTip/CustomToolTip';
import { compactPath } from '../../../utility/common';

interface IdeationOpportunitiesCardProps {
  title: string;
  tag?: string;
  description: string;
  complexity: 'High' | 'Medium' | 'Low';
  roi: 'High' | 'Medium' | 'Low';
  approvalStatus?: 'approval_pending' | 'approved' | 'created' | null;
  tagColor?: string;
  tagBgColor?: string;
  vault?: boolean;
  onClick?: () => void;
  clickable?: boolean;
  breadcrumb?: string;
}

const StyledCard = styled(Box)<{ clickable?: boolean }>(({ clickable }) => ({
  position: 'relative',

  width: '100%',
  display: 'flex',
  flexDirection: 'column',

  borderRadius: '7px',
  borderLeft: '1px solid #AAA9AE',
  borderRight: '1px solid #AAA9AE',
  borderBottom: '1px solid #AAA9AE',
  marginBottom: '16px',
  cursor: clickable ? 'pointer' : 'default',
  transition: 'all 0.2s ease',

  '&:hover': clickable ? { border: '1px solid #1A55F9' } : {},

  '@media (min-width: 2400px)': {
    flex: '1 1 calc(33.33% - 16px)',
  },

  '@media (min-width: 3300px)': {
    flex: '1 1 calc(25% - 16px)',
  },
}));

const HeaderBox = styled(Box)({
  background: 'linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%)',
  padding: '4px 12px',
  borderTopLeftRadius: '7px',
  borderTopRightRadius: '7px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '10px',
});

const WhiteCurveBox = styled(Box)<{ curvewidth: string }>(({ curvewidth }) => ({
  width: curvewidth,
  height: '28px',
  backgroundColor: '#FFFFFF',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
  position: 'absolute',
  top: '22px',
  right: '12px',
  transform: 'translateY(-50%)',
  zIndex: 11,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ApprovalBadge = styled(Box)<{ status?: string }>({
  height: '18px',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const TitleTypography = styled(Typography)({
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: 1.35,
  color: '#1A55F9',
  width: '100%',
  minWidth: 0,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const TagChip = styled(Chip)({
  height: '28px',
  borderRadius: '39px',

  fontFamily: 'Poppins',
  fontWeight: 600,
  fontSize: '13px',
  textTransform: 'uppercase',

  backgroundColor: '#FFFFFF',
  color: '#1C53F5',
  border: '1.5px solid #1C53F5',

  padding: '0 16px',

  '&:hover': {
    backgroundColor: '#FFFFFF',
  },

  '& .MuiChip-label': {
    padding: 0,
    color: '#1C53F5',
  },
});

const ContentBox = styled(Box)({
  padding: '20px 12px',
  backgroundColor: '#FFFFFF',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

const MetricsBox = styled(Box)({
  width: '220px',
  minWidth: '220px',
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  marginLeft: '24px',
});

const MetricItem = styled(Box)({
  width: '100%',
  padding: '10px 16px',
  borderRadius: '6px',
  border: '1px solid #ACADBE',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  flexDirection: 'column',
});

const MetricLabel = styled(Typography)({
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  color: '#6B7280',
  marginBottom: '4px',
});

const MetricValue = styled(Box)<{ value: string; isComplexity: boolean }>(
  ({ value, isComplexity }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '15px',
    '&::before': {
      content: '""',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor:
        value === 'High'
          ? isComplexity
            ? '#EF4444'
            : '#10B981'
          : value === 'Low'
            ? isComplexity
              ? '#10B981'
              : '#EF4444'
            : '#F59E0B',
    },
  })
);

const getApprovalBadgeConfig = (status?: string | null) => {
  if (!status) return { text: '', show: false };

  const normalized = String(status).trim().toLowerCase();

  switch (normalized) {
    case 'approval_pending':
      return {
        text: 'Approval pending',
        color: '#FF0505',
        bgColor: '#FFF5F2',
        borderColor: '#FF0505',
        show: true,
      };

    case 'approved':
      return {
        text: 'Approved',
        color: '#11953E',
        bgColor: '#DBFFE8',
        borderColor: '#11953E',
        show: true,
      };

    case 'solutioning':
      return {
        text: 'Solutioning',
        color: '#11953E',
        bgColor: '#DBFFE8',
        borderColor: '#11953E',
        show: true,
      };

    case 'ready_to_build':
      return {
        text: 'Ready to Build',
        color: '#626262',
        bgColor: 'rgba(169,169,169,0.16)',
        borderColor: '#626262',
        show: true,
      };

    case 'build':
      return {
        text: 'Build',
        color: '#11953E',
        bgColor: '#DBFFE8',
        borderColor: '#11953E',
        show: true,
      };

    default:
      return { text: '', show: false };
  }
};

const IdeationOpportunitiesCard: React.FC<IdeationOpportunitiesCardProps> = ({
  title,
  tag,
  description,
  complexity,
  roi,
  approvalStatus,
  vault = false,
  onClick,
  clickable = false,
  breadcrumb,
}) => {
  const badgeConfig = getApprovalBadgeConfig(approvalStatus);
  const curveWidth = (() => {
  switch (badgeConfig.text) {
    case 'Approval pending':
      return '130px';
    case 'Solutioning':
      return '90px';
    case 'Approved':
      return '80px';
    case 'Ready to Build':
      return '110px';
    case 'Build':
      return '50px';
    default:
      return '53px';
  }
})();

  return (
    <StyledCard onClick={onClick} clickable={clickable}>
      <HeaderBox />

      {badgeConfig.show && (
        <WhiteCurveBox curvewidth={curveWidth}>
          <ApprovalBadge sx={{ backgroundColor: badgeConfig.bgColor }}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '12px',
                color: badgeConfig.color,
              }}
            >
              {badgeConfig.text}
            </Typography>
          </ApprovalBadge>
        </WhiteCurveBox>
      )}

      <ContentBox>
        <CustomTooltip
          title={<Typography sx={{ fontSize: 12 }}>{breadcrumb || ''}</Typography>}
          variant="bottom"
          arrow
        >
          <Typography sx={{ color: '#737373', mb: '12px' }}>
            {compactPath(breadcrumb || '')}
          </Typography>
        </CustomTooltip>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
          mb={1}
          sx={{ width: '100%', minWidth: 0 }}
        >
          <Box sx={{ flex: 1, minWidth: 0, mr: 2, overflow: 'hidden' }}>
            <Box display="flex" alignItems="flex-start" gap={1} sx={{ minWidth: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TitleTypography>{title}</TitleTypography>
              </Box>
              {vault && (
                <Box
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#3730A3',
                    fontSize: '10px',
                    fontWeight: 600,
                    fontFamily: 'Poppins',
                    padding: '2px 6px',
                    borderRadius: '4px',
                  }}
                ></Box>
              )}
            </Box>
          </Box>
          {tag && (
            <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
              <TagChip label={tag} variant="outlined" />
            </Box>
          )}
        </Box>

        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', my: 1 }}>
          <Box sx={{ width: '100%', height: '1px', backgroundColor: '#AAA9AE' }} />
        </Box>

        <Box display="flex" mt={2} alignItems="flex-start">
          <Box
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 400,
              fontSize: '16px',
              color: '#000000',
              flex: 1,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {description}
          </Box>

          <MetricsBox>
            <MetricItem>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <MetricLabel>Complexity</MetricLabel>
                  <MetricValue value={complexity} isComplexity={true}>
                    {complexity}
                  </MetricValue>
                </Box>

                <Box
                  sx={{
                    width: '1px',
                    height: '40px',
                    backgroundColor: '#E5E7EB',
                    mx: 2,
                  }}
                />

                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  <MetricLabel>ROI</MetricLabel>
                  <MetricValue value={roi} isComplexity={false}>
                    {roi}
                  </MetricValue>
                </Box>
              </Box>
            </MetricItem>
          </MetricsBox>
        </Box>
      </ContentBox>
    </StyledCard>
  );
};

export default IdeationOpportunitiesCard;
